import classNames from 'classnames';
import React, { CSSProperties, EventHandler, FC, ReactNode } from 'react';
import styled, { Interpolation } from 'styled-components';

import { Spinner } from '../spinner';

const Variant = {
    text: 'button-text',
    outlined: 'button-outlined',
    secondaryOutlined: 'button-secondary-outlined',
    contained: 'button-contained',
};

const Size = {
    small: 'button-small',
    medium: 'button-medium',
    large: 'button-large',
};

interface ButtonProps {
    children?: ReactNode;
    color?: string;
    disabled?: boolean;
    endIcon?: ReactNode;
    fullWidth?: boolean;
    loading?: boolean;
    size?: 'small' | 'medium' | 'large';
    startIcon?: ReactNode;
    variant?: 'text' | 'outlined' | 'secondaryOutlined' | 'contained';
    styles?: Interpolation<CSSProperties>;
    onClick?: EventHandler<any>;
    className?: string;
    type?: 'submit' | 'button';
}

const StyleButton = styled.button<ButtonProps>`
    ${({ theme: { colors, typography }, color }) => `
        &.button {
            font-family: inherit;
            color: ${colors.text.accent};
            font-size: ${typography.size.desktop.paragraph.p1};
            line-height: calc(${typography.lineHeight[3]} - 0.5px);
            font-weight: ${typography.weight.medium};
            border-radius: 100px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            white-space: nowrap;
        }
        &.button-small {
            font-size: ${typography.size.desktop.paragraph.p3};
            line-height: ${typography.lineHeight[5]};
            padding: 4px 12px;
        }
        &.button-medium {
            font-size: ${typography.size.desktop.paragraph.p2};
            line-height: ${typography.lineHeight[6]};
            padding: 8px 24px;
        }
        &.button-large {
            font-size: ${typography.size.desktop.paragraph.p1};
            line-height: ${typography.lineHeight[7]};
            padding: 12px 36px;
        }
        &.button-text {
            border: 0;
            background: none;
        }
        &.button-secondary-outlined {
            border: 1px solid ${colors.border.light};
            background: none;
            color: ${colors.text.primary};
            &:hover{
                border-color: ${colors.primary} !important;
            }
        }
        &.button-outlined {
            background: linear-gradient(${colors.background.primary} 0 0) padding-box, ${
        colors.brand.accent
    } border-box;
            border: 1px solid transparent;
            color: ${colors.text.primary};
            &:hover{
                border-color: ${colors.primary} !important;
            }
        }
        &.button-contained {
            border: ${colors.background.primary};
            background: ${colors.background.accent};
        }
        &.button-disabled {
            color: ${colors.text.disabled};
            background: ${colors.background.disabled};
            cursor: not-allowed;
        }
        &.button-fullWidth {
            width: 100%;
        }
        ${color ? `color: ${color} !important;` : ''}
    `}
    ${({ styles }) => styles};
`;

const Button: FC<ButtonProps> = ({
    children,
    className,
    startIcon,
    endIcon,
    styles,
    loading,
    onClick,
    disabled = false,
    size = 'medium',
    variant = 'contained',
    fullWidth,
    color,
    type = 'submit',
}) => {
    return (
        <StyleButton
            className={classNames(
                'button',
                Size[size],
                Variant[variant],
                fullWidth && 'button-fullWidth',
                disabled && 'button-disabled',
                className,
            )}
            onClick={onClick}
            styles={styles}
            fullWidth={fullWidth}
            color={color}
            disabled={disabled}
            type={type}
        >
            {loading ? (
                <Spinner size="20" />
            ) : (
                <>
                    {startIcon && startIcon}
                    {children}
                    {endIcon && endIcon}
                </>
            )}
        </StyleButton>
    );
};

export default Button;
